<template>
    <transition
        :enter-active-class="enterActiveClass"
        enter-class="opacity-0"
        enter-to-class="opacity-100"
        :leave-active-class="leaveActiveClass"
        leave-class="opacity-100"
        leave-to-class="opacity-0"
        @after-enter="$emit('opened')"
        @after-leave="$emit('closed')"
        @before-enter="$emit('open')"
        @before-leave="$emit('close')"
    >
        <span
            class="absolute z-30"
            :class="[className, { 'min-w-full': fullWidth }]"
            ref="content"
            v-show="opened"
        >
            <div :class="innerClassName">
                <slot />
            </div>
        </span>
    </transition>
</template>

<script>
    import { createPopper } from '@popperjs/core';
    import { elementContains } from '@/utils/dom.js';

    export default {
        props: {
            placement: {
                type: String,
                default: 'bottom',
                validator: (value) => [
                    'top', 'top-start', 'top-end',
                    'bottom', 'bottom-start', 'bottom-end',
                    'right', 'right-start', 'right-end',
                    'left', 'left-start', 'left-end',
                ].includes(value),
            },
            opened: {
                type: Boolean,
                default: false,
            },
            offset: {
                type: Number,
                default: 6,
            },
            className: {
                type: String,
                default: 'rounded shadow-lg bg-white border border-gray-200',
            },
            innerClassName: {
                type: String,
                default: '',
            },
            trigger: {
                type: null,
                required: true,
            },
            enterActiveClass: {
                type: String,
                default: 'transition-opacity ease-out duration-200',
            },
            leaveActiveClass: {
                type: String,
                default: 'transition-opacity ease-in duration-100',
            },
            fullWidth: {
                type: Boolean,
                default: true,
            },
        },

        data: () => ({
            popper: null,
        }),

        watch: {
            opened(isOpened) {
                if (isOpened) {
                    this.updatePopper();
                }
            },
        },

        methods: {
            updatePopper() {
                this.popper ? this.popper.update() : this.createPopper();
            },

            createPopper() {
                this.$nextTick(() => {
                    const trigger = this.trigger.$el || this.trigger;

                    this.popper = createPopper(trigger, this.$refs.content, {
                        placement: this.placement,
                        modifiers: [
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, this.offset],
                                },
                            },
                            {
                                name: 'preventOverflow',
                                options: {
                                    padding: 16,
                                },
                            },
                        ],
                    });
                });
            },

            destroyPopper() {
                if (this.popper) {
                    this.popper.destroy();
                    this.popper = null;
                }
            },

            focus() {
                this.$nextTick(() => {
                    this.$refs.content.focus();
                });
            },

            handleDocumentClick(e) {
                if (elementContains(this.$refs.content, e.target)) {
                    this.$emit('clickin', e);
                } else {
                    this.$emit('clickout', e);
                }
            },
        },

        mounted() {
            document.addEventListener('click', this.handleDocumentClick);
        },

        beforeDestroy() {
            document.removeEventListener('click', this.handleDocumentClick);
            this.destroyPopper();
        },
    };
</script>
